/**
 * CategoryPreview Component
 *
 * Preview component for category field type.
 * Shows a visual representation of a product category grid/list.
 *
 * Responsive breakpoints (border-box widths, include padding+border):
 * - Narrow (<276px): Single column, compact layout, 7-9px fonts
 * - Medium (276-484px): Optimized layout, 10-12px fonts
 * - Normal (>=484px): Full layout with all features
 */

import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { CategoryPreviewProps } from './types';
import { CategoryIcon } from 'assests/GmsIcons';
import { CategoryImageDisplay } from '../../config/fieldDefaults';
import Checkbox from 'Components/DesignSystem/Checkbox/Checkbox';
import { useModule } from '../../contexts/ModuleContext';
import { useOscillationSafeWidth } from '../../hooks/useOscillationSafeWidth';
import { useCategoryAttributesCache } from '../../contexts/CategoryAttributesCacheContext';
import { getProductsByCategory } from '../../services/ApiHelper';

// ============================================================================
// Product Data Types & Sample Fallback
// ============================================================================

interface ProductData {
  id: string;
  name: string;
  description: string;
  min_ad_price: number;
  category: string;
  Brand?: string;
  model_number?: string;
  upc?: string;
  version?: string;
  [key: string]: any;
}

const SAMPLE_PRODUCTS: ProductData[] = [
  {
    id: 'sample-1',
    name: 'PowerFlex 525 AC Drive 1HP',
    model_number: 'YC1K-5D24',
    upc: '25B-D2P3N114',
    description: 'Compact, cost-effective variable frequency drive for simple standalone applications.',
    min_ad_price: 342.00,
    Brand: 'Allen-Bradley',
    category: 'AC Drives',
  },
  {
    id: 'sample-2',
    name: 'IEC Contactor 9A 24VDC Coil',
    model_number: 'LC1D09BL',
    upc: '100-C09D10',
    description: 'Full-voltage, non-reversing contactor for motor starting applications up to 5HP.',
    min_ad_price: 87.50,
    Brand: 'Schneider Electric',
    category: 'Contactors',
  },
  {
    id: 'sample-3',
    name: 'Miniature Circuit Breaker 20A',
    model_number: 'FAZ-C20/1',
    upc: 'FAZ-C20-1-SP',
    description: 'Supplementary protector for branch circuit protection in control panels.',
    min_ad_price: 24.90,
    Brand: 'Eaton',
    category: 'Circuit Breakers',
  },
  {
    id: 'sample-4',
    name: 'Safety Relay Module 24VDC',
    model_number: 'MSR127TP',
    upc: '440R-N23132',
    description: 'Dual-channel safety relay for emergency stop and safety gate monitoring.',
    min_ad_price: 189.00,
    Brand: 'Allen-Bradley',
    category: 'Safety Relays',
  },
];

// ============================================================================
// Inline SVG Icons
// ============================================================================

const ImagePlaceholderIcon = ({ className = 'tw-w-6 tw-h-6' }: { className?: string }) => (
  <svg className={`${className} tw-text-ink-tertiary`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const CheckCircleIcon = ({ className = 'tw-w-3 tw-h-3' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7.5" fill="#36B37E" stroke="#36B37E" />
    <path d="M5 8l2 2 4-4" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const BackOrderIcon = ({ className = 'tw-w-3 tw-h-3' }: { className?: string }) => (
  <svg className={className} viewBox="0 0 16 16" fill="none">
    <circle cx="8" cy="8" r="7.5" fill="#FFC400" stroke="#FFC400" />
    <path d="M8 5v4M8 11h.01" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

export const CategoryPreview = ({
  config,
  isSelected,
  onClick,
  categoryId,
}: CategoryPreviewProps) => {
  const {
    showTitle,
    titleText,
    label,
    showCategoryDescription,
    descriptionText,
    showThumbnail,
    imageDisplay,
    categoryImageUrl,
    style,
    productsPerRow,
    showModelNumber,
    showProductDescription,
    showPrice,
    showUOM,
    showAvailabilityStatus,
    inStockLabel,
    backOrderLabel,
    productDetailsTextAlignment,
    showInStock,
    inStockText,
    selectByDefault,
    showCategory,
    showManufacturer,
    descriptionStyle,
    showButtonText,
    buttonLabel,
    showButtonIcon,
    buttonIconUrl,
  } = config;

  // Responsive container width tracking (oscillation-safe: breakpoint flips
  // that change our own measured width must not loop — React error #185)
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, updateContainerWidth] = useOscillationSafeWidth();

  // Collapsed description expand/collapse state (tracks expanded product indices)
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set());

  // Product data state — starts empty, shows loader until API responds
  const [products, setProducts] = useState<ProductData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { industryId } = useModule();
  const { setAttributes } = useCategoryAttributesCache();

  // Fetch real products when categoryId and industryId are available
  const fetchProducts = useCallback(async () => {
    if (!categoryId || !industryId) {
      setProducts(SAMPLE_PRODUCTS);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getProductsByCategory(industryId, categoryId);
      if (response?.done && response.data?.products?.length > 0) {
        setProducts(
          response.data.products.map((p: any) => ({
            ...p,
            min_ad_price: Number(p.min_ad_price) || 0,
          }))
        );
      } else {
        // API returned no products — fall back to sample data
        setProducts(SAMPLE_PRODUCTS);
      }
      // Store attributes in shared cache for ConfigPanel
      if (response?.done && response.data?.attributes) {
        setAttributes(categoryId, response.data.attributes);
      }
    } catch (error) {
      // Fall back to sample products on error
      console.error('Failed to fetch products for category:', error);
      setProducts(SAMPLE_PRODUCTS);
    } finally {
      setIsLoading(false);
    }
  }, [categoryId, industryId, setAttributes]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Synchronous measurement before paint — catches state-driven width changes
  // immediately so responsive breakpoints are correct on the first paint frame.
  useLayoutEffect(() => {
    if (containerRef.current) {
      updateContainerWidth(containerRef.current.getBoundingClientRect().width);
    }
  });

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateContainerWidth(entry.target.getBoundingClientRect().width);
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, [updateContainerWidth]);

  // Responsive breakpoint detection
  const isNarrowContainer = containerWidth > 0 && containerWidth < 276;
  const isMediumContainer = containerWidth >= 276 && containerWidth < 484;

  const productsCount = parseInt(productsPerRow, 10) || 3;

  // Adjust product count based on container width
  const responsiveProductsCount = isNarrowContainer
    ? 1
    : isMediumContainer
      ? Math.min(2, productsCount)
      : productsCount;

  const displayProducts = products.slice(
    0,
    style === 'List' ? (isNarrowContainer ? 1 : 2) : responsiveProductsCount
  );

  const getAlignmentClass = () => {
    switch (productDetailsTextAlignment) {
      case 'Centre':
        return 'tw-text-center';
      case 'Right':
        return 'tw-text-right';
      default:
        return 'tw-text-left';
    }
  };

  // ============================================================================
  // Stock Status Renderer
  // ============================================================================

  const renderStockStatus = (product: ProductData, sizeClass: 'sm' | 'md' | 'lg') => {
    if (!showAvailabilityStatus) return null;
    const isInStock = true; // API doesn't return stock status; default to in-stock
    const labelText = isInStock ? inStockLabel : backOrderLabel;
    const iconSize = sizeClass === 'sm' ? 'tw-w-2 tw-h-2' : sizeClass === 'md' ? 'tw-w-2.5 tw-h-2.5' : 'tw-w-3 tw-h-3';
    const textSize = sizeClass === 'sm' ? 'tw-text-[7px]' : sizeClass === 'md' ? 'tw-text-[8px]' : 'tw-text-xs';

    return (
      <span className="tw-flex tw-items-center tw-gap-1">
        {isInStock ? <CheckCircleIcon className={iconSize} /> : <BackOrderIcon className={iconSize} />}
        <span className={`${textSize} ${isInStock ? 'tw-text-tone-success-glyph' : 'tw-text-tone-warning-glyph'}`}>
          {labelText}
        </span>
      </span>
    );
  };

  // ============================================================================
  // Add to Cart Button
  // ============================================================================

  const renderAddToCartButton = (sizeClass: 'sm' | 'md' | 'lg', isListView = false) => {
    const hasText = showButtonText !== false;
    const hasIcon = showButtonIcon && buttonIconUrl;
    if (!hasText && !hasIcon) return null;

    const text = hasText
      ? (sizeClass === 'sm' ? (buttonLabel ? buttonLabel.substring(0, 6) : 'Add') : (buttonLabel || 'Add to Cart'))
      : null;
    const iconSize = sizeClass === 'sm' ? 'tw-w-3 tw-h-3' : sizeClass === 'md' ? 'tw-w-3.5 tw-h-3.5' : 'tw-w-4 tw-h-4';
    const icon = hasIcon ? <img src={buttonIconUrl} alt="" className={`${iconSize} tw-object-contain`} /> : null;

    const baseClass = isListView
      ? (sizeClass === 'sm'
        ? 'tw-text-[8px] tw-px-2 tw-py-0.5'
        : sizeClass === 'md'
          ? 'tw-text-[8px] tw-px-2 tw-py-0.5 tw-whitespace-nowrap'
          : 'tw-text-xs tw-px-3 tw-py-1 tw-whitespace-nowrap')
      : (sizeClass === 'sm'
        ? 'tw-text-[8px] tw-px-2 tw-py-0.5 tw-mt-1'
        : sizeClass === 'md'
          ? 'tw-text-[9px] tw-px-2 tw-py-0.5 tw-mt-1.5'
          : 'tw-text-xs tw-px-3 tw-py-1 tw-mt-2');

    return (
      <button data-testid="category-categorypreview-button-1" className={`${baseClass} tw-border tw-border-edge-strong tw-rounded tw-text-ink tw-bg-surface ${isListView ? '' : 'tw-w-full'} tw-flex tw-items-center tw-justify-center tw-gap-1`}>
        {icon}
        {text}
      </button>
    );
  };

  // ============================================================================
  // Product Meta Fields (category, manufacturer, variants)
  // ============================================================================

  const renderProductMeta = (
    product: ProductData,
    textSize: string,
    descriptionNode?: React.ReactNode
  ) => {
    const items: React.ReactNode[] = [];

    // Order matches runtime Product Details: Manufacturer → Category → Description.
    if (showManufacturer) {
      items.push(
        <p key="mfr" className={`${textSize} tw-text-ink-secondary`}>{product.Brand || 'N/A'}</p>
      );
    }
    if (showCategory) {
      items.push(
        <p key="cat" className={`${textSize} tw-text-ink-secondary`}>{product.category}</p>
      );
    }
    if (descriptionNode) {
      items.push(<React.Fragment key="desc">{descriptionNode}</React.Fragment>);
    }
    return items.length > 0 ? <>{items}</> : null;
  };

  // ============================================================================
  // Product Description (matches old UI-Model behavior)
  // ============================================================================

  const toggleExpanded = (index: number) => {
    setExpandedProducts(prev => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const renderDescription = (product: ProductData, index: number, textSize: string) => {
    if (!showProductDescription) return null;

    const description = product.description || '';
    if (!description) return null;

    switch (descriptionStyle) {
      case 'Partly': {
        const truncatedText = description.length > 100
          ? description.substring(0, 100) + '...'
          : description;
        return (
          <p className={`${textSize} tw-text-ink-secondary`}>
            {truncatedText}
          </p>
        );
      }
      case 'Full':
        return (
          <p className={`${textSize} tw-text-ink-secondary`}>
            {description}
          </p>
        );
      case 'Collapsed': {
        const isExpanded = expandedProducts.has(index);
        return (
          <div>
            <p className={`${textSize} tw-text-ink-secondary`}>
              {isExpanded ? description : description.substring(0, 50) + '...'}
            </p>
            {description.length > 50 && (
              <button data-testid="category-categorypreview-button-2"
                onClick={(e) => { e.stopPropagation(); toggleExpanded(index); }}
                className={`${textSize} tw-text-accent tw-font-medium hover:tw-text-accent-hover tw-mt-0.5`}
              >
                {isExpanded ? 'Show less' : 'Show more'}
              </button>
            )}
          </div>
        );
      }
      default:
        return (
          <p className={`${textSize} tw-text-ink-secondary`}>
            {description}
          </p>
        );
    }
  };

  // ============================================================================
  // Grid Views
  // ============================================================================

  const renderGridView = () => {
    // Narrow container: compact single-product layout
    if (isNarrowContainer) {
      return (
        <div className="tw-space-y-1.5">
          {displayProducts.map((product, index) => (
            <div key={index} className="tw-border tw-border-edge tw-rounded tw-p-1.5 tw-bg-surface">
              <div className="tw-flex tw-items-start tw-gap-1.5 tw-mb-1">
                <div className="tw-w-10 tw-h-10 tw-bg-surface-sunken tw-rounded tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                  <ImagePlaceholderIcon className="tw-w-4 tw-h-4" />
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  {showModelNumber && (
                    <p className="tw-text-[7px] tw-text-ink-secondary tw-break-all">{product.model_number}</p>
                  )}
                  <p className="tw-text-[9px] tw-font-bold tw-text-ink tw-line-clamp-2">{product.name}</p>
                  {renderStockStatus(product, 'sm')}
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-justify-between">
                {showPrice && (
                  <span className="tw-text-[9px] tw-font-semibold tw-text-ink">${product.min_ad_price?.toFixed(2)}{showUOM && <span className="tw-font-normal tw-text-ink-secondary">/ per Unit</span>}</span>
                )}
              </div>
              {renderAddToCartButton('sm')}
            </div>
          ))}
        </div>
      );
    }

    // Medium container: optimized 2-column layout
    if (isMediumContainer) {
      return (
        <div
          className="tw-grid tw-gap-2"
          style={{ gridTemplateColumns: `repeat(${Math.min(2, responsiveProductsCount)}, 1fr)` }}
        >
          {displayProducts.map((product, index) => (
            <div key={index} className="tw-border tw-border-edge tw-rounded tw-p-1.5 tw-bg-surface tw-flex tw-flex-col">
              <div className="tw-w-full tw-h-12 tw-bg-surface-sunken tw-rounded tw-mb-1.5 tw-flex tw-items-center tw-justify-center">
                <ImagePlaceholderIcon className="tw-w-5 tw-h-5" />
              </div>
              <div className={`${getAlignmentClass()} tw-flex-1`}>
                {showModelNumber && (
                  <p className="tw-text-[8px] tw-text-ink-secondary tw-break-all">{product.model_number}</p>
                )}
                <p className="tw-text-[10px] tw-font-bold tw-text-ink tw-line-clamp-2">{product.name}</p>
                {renderProductMeta(product, 'tw-text-[8px]', renderDescription(product, index, 'tw-text-[8px]'))}
                {showPrice && (
                  <p className="tw-text-[10px] tw-font-semibold tw-text-ink tw-mt-0.5">${product.min_ad_price?.toFixed(2)}{showUOM && <span className="tw-font-normal tw-text-ink-secondary">/ per Unit</span>}</p>
                )}
                {renderStockStatus(product, 'md')}
              </div>
              {renderAddToCartButton('md')}
            </div>
          ))}
        </div>
      );
    }

    // Normal container: full layout
    return (
      <div
        className="tw-grid tw-gap-3"
        style={{ gridTemplateColumns: `repeat(${productsCount}, 1fr)` }}
      >
        {displayProducts.map((product, index) => (
          <div key={index} className="tw-border tw-border-edge tw-rounded tw-p-2 tw-bg-surface tw-flex tw-flex-col">
            {/* Product Image */}
            <div className="tw-w-full tw-h-16 tw-bg-surface-sunken tw-rounded tw-mb-2 tw-flex tw-items-center tw-justify-center">
              <ImagePlaceholderIcon className="tw-w-6 tw-h-6" />
            </div>
            <div className={`${getAlignmentClass()} tw-flex-1`}>
              {showModelNumber && (
                <p className="tw-text-[10px] tw-text-ink-secondary tw-break-all">{product.model_number}</p>
              )}
              <p className="tw-text-xs tw-font-bold tw-text-ink tw-line-clamp-3">{product.name}</p>
              {renderProductMeta(product, 'tw-text-[10px]', renderDescription(product, index, 'tw-text-xs tw-mt-0.5'))}
              {showPrice && (
                <p className="tw-text-xs tw-font-semibold tw-text-ink tw-mt-1">${product.min_ad_price?.toFixed(2)}{showUOM && <span className="tw-font-normal tw-text-ink-secondary">/ per Unit</span>}</p>
              )}
              {renderStockStatus(product, 'lg')}
            </div>
            {renderAddToCartButton('lg')}
          </div>
        ))}
      </div>
    );
  };

  // ============================================================================
  // List Views
  // ============================================================================

  const renderListView = () => {
    // Narrow container: compact stacked layout
    if (isNarrowContainer) {
      return (
        <div className="tw-space-y-1.5">
          {displayProducts.map((product, index) => (
            <div key={index} className="tw-border tw-border-edge tw-rounded tw-p-1.5 tw-bg-surface">
              <div className="tw-flex tw-items-start tw-gap-1.5 tw-mb-1">
                <div className="tw-w-11 tw-h-11 tw-bg-surface-sunken tw-rounded tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                  <ImagePlaceholderIcon className="tw-w-4 tw-h-4" />
                </div>
                <div className="tw-flex-1 tw-min-w-0">
                  {showModelNumber && (
                    <p className="tw-text-[7px] tw-text-ink-secondary tw-break-all">{product.model_number}</p>
                  )}
                  <p className="tw-text-[9px] tw-font-bold tw-text-ink tw-line-clamp-2">{product.name}</p>
                  {renderStockStatus(product, 'sm')}
                </div>
              </div>
              <div className="tw-flex tw-items-center tw-justify-between">
                {showPrice && (
                  <span className="tw-text-[9px] tw-font-semibold tw-text-ink">${product.min_ad_price?.toFixed(2)}{showUOM && <span className="tw-font-normal tw-text-ink-secondary">/ per Unit</span>}</span>
                )}
                {renderAddToCartButton('sm', true)}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Medium container: compact horizontal layout
    if (isMediumContainer) {
      return (
        <div className="tw-space-y-1.5">
          {displayProducts.map((product, index) => (
            <div key={index} className="tw-flex tw-gap-2 tw-border tw-border-edge tw-rounded tw-p-1.5 tw-bg-surface">
              <div className="tw-w-14 tw-h-14 tw-bg-surface-sunken tw-rounded tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
                <ImagePlaceholderIcon className="tw-w-5 tw-h-5" />
              </div>
              <div className="tw-flex-1 tw-min-w-0">
                {showModelNumber && (
                  <p className="tw-text-[8px] tw-text-ink-secondary tw-break-all">{product.model_number}</p>
                )}
                <p className="tw-text-[10px] tw-font-bold tw-text-ink tw-line-clamp-2">{product.name}</p>
                {renderProductMeta(product, 'tw-text-[8px]', renderDescription(product, index, 'tw-text-[8px]'))}
                <div className="tw-flex tw-items-center tw-gap-1.5 tw-mt-0.5">
                  {showPrice && (
                    <span className="tw-text-[10px] tw-font-semibold tw-text-ink">${product.min_ad_price?.toFixed(2)}{showUOM && <span className="tw-font-normal tw-text-ink-secondary">/ per Unit</span>}</span>
                  )}
                  {renderStockStatus(product, 'md')}
                </div>
              </div>
              <div className="tw-flex tw-flex-col tw-justify-end tw-flex-shrink-0">
                {renderAddToCartButton('md', true)}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Normal container: full layout
    return (
      <div className="tw-space-y-2">
        {displayProducts.map((product, index) => (
          <div key={index} className="tw-flex tw-gap-3 tw-border tw-border-edge tw-rounded tw-p-2 tw-bg-surface">
            {/* Product Image */}
            <div className="tw-w-16 tw-h-16 tw-bg-surface-sunken tw-rounded tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center">
              <ImagePlaceholderIcon className="tw-w-6 tw-h-6" />
            </div>
            <div className="tw-flex-1 tw-min-w-0">
              {showModelNumber && (
                <p className="tw-text-[10px] tw-text-ink-secondary tw-break-all">{product.model_number}</p>
              )}
              <p className="tw-text-sm tw-font-bold tw-text-ink tw-line-clamp-2">{product.name}</p>
              {renderProductMeta(product, 'tw-text-[10px]', renderDescription(product, index, 'tw-text-xs tw-mt-0.5'))}
              <div className="tw-flex tw-items-center tw-gap-2 tw-mt-1">
                {showPrice && (
                  <span className="tw-text-sm tw-font-semibold tw-text-ink">${product.min_ad_price?.toFixed(2)}{showUOM && <span className="tw-font-normal tw-text-ink-secondary">/ per Unit</span>}</span>
                )}
                {renderStockStatus(product, 'lg')}
              </div>
            </div>
            <div className="tw-flex tw-flex-col tw-justify-end tw-flex-shrink-0">
              {renderAddToCartButton('lg', true)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // ============================================================================
  // Header: In Stock Checkbox
  // ============================================================================

  const renderInStockCheckbox = (sizeClass: 'sm' | 'md' | 'lg') => {
    if (!showInStock) return null;

    // lg: Use design system Checkbox
    if (sizeClass === 'lg') {
      return (
        <Checkbox
          label={inStockText || 'Show In Stock only'}
          checked={selectByDefault}
          onChange={() => {}}
          className="tw-flex-shrink-0"
        />
      );
    }

    // sm/md: Compact inline checkbox (design system too large for these sizes)
    const checkboxSize = sizeClass === 'sm' ? 'tw-w-2.5 tw-h-2.5' : 'tw-w-3 tw-h-3';
    const textSize = sizeClass === 'sm' ? 'tw-text-[7px]' : 'tw-text-[9px]';

    return (
      <label className="tw-flex tw-items-center tw-gap-1 tw-flex-shrink-0 tw-cursor-default">
        <span
          className={`${checkboxSize} tw-rounded-sm tw-border tw-flex tw-items-center tw-justify-center ${
            selectByDefault
              ? 'tw-bg-accent-bold tw-border-accent-bold'
              : 'tw-border-edge-strong tw-bg-surface'
          }`}
        >
          {selectByDefault && (
            <svg className="tw-w-full tw-h-full tw-text-ink-inverse" viewBox="0 0 12 12" fill="none">
              <path d="M3 6l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </span>
        <span className={`${textSize} tw-text-ink tw-whitespace-nowrap`}>
          {inStockText || 'Show In Stock only'}
        </span>
      </label>
    );
  };

  // ============================================================================
  // Header: Thumbnail (inline with header)
  // ============================================================================

  const getImageDisplayClass = (display: CategoryImageDisplay): string => {
    switch (display) {
      case 'Fit': return 'tw-object-contain';
      case 'Fill': return 'tw-object-cover';
      case 'Stretch': return 'tw-w-full tw-h-full';
      default: return 'tw-object-contain';
    }
  };

  const [imageError, setImageError] = useState(false);

  // Reset error state when URL changes
  useEffect(() => {
    setImageError(false);
  }, [categoryImageUrl]);

  const renderInlineThumbnail = (size: string, iconSize: string = 'tw-w-4 tw-h-4') => {
    if (!showThumbnail) return null;

    if (categoryImageUrl && !imageError) {
      const fitClass = imageDisplay === 'Stretch' ? '' : getImageDisplayClass(imageDisplay);
      const stretchClass = imageDisplay === 'Stretch' ? 'tw-w-full tw-h-full' : '';
      return (
        <div className={`${size} tw-flex-shrink-0 tw-overflow-hidden tw-rounded`}>
          <img
            src={categoryImageUrl}
            alt="Category"
            className={`${fitClass} ${stretchClass} tw-w-full tw-h-full`}
            onError={() => setImageError(true)}
          />
        </div>
      );
    }

    return (
      <div className={`${size} tw-bg-surface-sunken tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center`}>
        <ImagePlaceholderIcon className={iconSize} />
      </div>
    );
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <div role="button" data-testid="category-categorypreview-div-1"
      ref={containerRef}
      className={`tw-border tw-rounded tw-bg-surface tw-cursor-pointer tw-transition-all tw-overflow-hidden ${
        isNarrowContainer ? 'tw-p-2' : isMediumContainer ? 'tw-p-3' : 'tw-p-4'
      } ${
        isSelected
          ? 'tw-border-accent tw-ring-2 tw-ring-accent-tint'
          : 'tw-border-edge hover:tw-border-edge-strong'
      }`}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {/* Category Header - responsive */}
      {isNarrowContainer ? (
        <div className="tw-mb-2">
          {showTitle && (
            <div className="tw-flex tw-items-center tw-gap-1">
              <span className="tw-w-3 tw-h-3 tw-flex-shrink-0 tw-text-ink-secondary [&_path]:tw-stroke-current">
                <CategoryIcon />
              </span>
              <span className="tw-text-[9px] tw-font-semibold tw-text-ink tw-line-clamp-1 tw-flex-1">
                {titleText || label || 'Category'}
              </span>
            </div>
          )}
          {showCategoryDescription && descriptionText && (
            <p className="tw-text-[7px] tw-text-ink-secondary tw-line-clamp-1 tw-mt-0.5" dangerouslySetInnerHTML={{ __html: descriptionText }} />
          )}
        </div>
      ) : isMediumContainer ? (
        <div className="tw-mb-2">
          <div className="tw-flex tw-items-center tw-gap-1.5">
            {showTitle && (
              <>
                <span className="tw-w-4 tw-h-4 tw-flex-shrink-0 tw-text-ink-secondary [&_path]:tw-stroke-current">
                  <CategoryIcon />
                </span>
                {renderInlineThumbnail('tw-w-10 tw-h-10 tw-rounded')}
                <span className="tw-text-[11px] tw-font-semibold tw-text-ink tw-flex-1 tw-min-w-0 tw-truncate">
                  {titleText || label || 'Category'}
                </span>
              </>
            )}
            {renderInStockCheckbox('md')}
          </div>
          {showCategoryDescription && descriptionText && (
            <p className="tw-text-[9px] tw-text-ink-secondary tw-mt-0.5 tw-line-clamp-2" dangerouslySetInnerHTML={{ __html: descriptionText }} />
          )}
        </div>
      ) : (
        <div className="tw-mb-3">
          <div className="tw-flex tw-items-center tw-gap-3">
            {showTitle && (
              <>
                <span className="tw-text-ink-secondary [&_path]:tw-stroke-current"><CategoryIcon /></span>
                {renderInlineThumbnail('tw-w-[68px] tw-h-[68px] tw-rounded-md tw-border tw-border-edge', 'tw-w-8 tw-h-8')}
                <div className="tw-flex-1 tw-min-w-0">
                  <p className="tw-text-sm tw-font-medium tw-leading-tight tw-text-ink">
                    {titleText || label || 'Category'}
                  </p>
                  {showCategoryDescription && descriptionText && (
                    <p className="tw-text-[10px] tw-text-ink-secondary tw-font-normal tw-leading-tight tw-mt-1" dangerouslySetInnerHTML={{ __html: descriptionText }} />
                  )}
                </div>
              </>
            )}
            {renderInStockCheckbox('lg')}
          </div>
        </div>
      )}

      {/* Products Grid/List */}
      {isLoading ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-py-8">
          <div className="tw-animate-spin tw-w-6 tw-h-6 tw-border-2 tw-border-edge tw-border-t-accent tw-rounded-full" />
        </div>
      ) : (
        style === 'Grid' ? renderGridView() : renderListView()
      )}
    </div>
  );
};

export default CategoryPreview;
